/**
 * Shared SEO title/description helpers for SSR + client Helmet.
 * Prefer keyword-led copy (Improvement Specs) over emoji branding.
 */

const CATEGORY_META = {
  sports: {
    en: {
      title: 'Sports Prediction Markets — Bet on Football, NBA, UFC, F1 | Futuur',
      description:
        'Make predictions on live sports events. See real-time odds on football, basketball, MMA, Formula 1, and more. Free to play, real money optional.',
    },
    pt: {
      title: 'Mercados de previsão esportiva — Futebol, NBA, UFC, F1 | Futuur',
      description:
        'Faça previsões em eventos esportivos ao vivo. Odds em tempo real para futebol, basquete, MMA, Fórmula 1 e mais. Grátis para jogar.',
    },
  },
  politics: {
    en: {
      title: 'Political Prediction Markets — Elections, Policy, World Events | Futuur',
      description:
        'Who will win the next election? Trade on political outcomes with real-time odds from thousands of forecasters. More accurate than polls.',
    },
    pt: {
      title: 'Mercados de previsão política — Eleições e geopolítica | Futuur',
      description:
        'Quem vai ganhar a próxima eleição? Negocie em resultados políticos com odds em tempo real da inteligência coletiva.',
    },
  },
  crypto: {
    en: {
      title: 'Crypto Prediction Markets — Bitcoin, Ethereum & More | Futuur',
      description:
        'Forecast crypto prices and milestones with crowd-powered prediction markets. Trade with play money or USDC on Futuur.',
    },
    pt: {
      title: 'Mercados de previsão cripto — Bitcoin, Ethereum e mais | Futuur',
      description:
        'Preveja preços e marcos cripto com mercados baseados em inteligência coletiva. Aposte com moeda fictícia ou USDC.',
    },
  },
}

/**
 * @param {string} slugOrTitle
 * @param {'en'|'pt'} locale
 * @returns {{ title: string, description: string } | null}
 */
export function getCategorySeoOverride(slugOrTitle, locale = 'en') {
  const key = String(slugOrTitle || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
  const localeKey = locale === 'pt' ? 'pt' : 'en'
  for (const [slug, copy] of Object.entries(CATEGORY_META)) {
    if (key.includes(slug)) return copy[localeKey]
  }
  return null
}

/**
 * @param {{ marketCount?: string|number, isPtBr?: boolean }} opts
 */
export function getHomeSeoMeta({ marketCount, isPtBr = false } = {}) {
  const count = marketCount || '30,000+'
  if (isPtBr) {
    return {
      title: 'Futuur — Mercados de previsão para esportes, política, cripto e mais',
      description: `Negocie em eventos do mundo real com dinheiro fictício ou USDC. Eleições, esportes, cripto, IA e mais. Junte-se a milhares de previsores em mais de ${count} mercados.`,
    }
  }
  return {
    title: 'Futuur — Prediction Markets for Sports, Politics, Crypto & More',
    description: `Trade on real-world events with play money or USDC. Bet on elections, sports, crypto prices, AI milestones and more. Explore over ${count} crowd-powered markets.`,
  }
}

/**
 * @param {{ categoryName: string, marketCount?: string|number, isPtBr?: boolean, slug?: string }} opts
 */
export function getCategorySeoMeta({ categoryName, marketCount, isPtBr = false, slug } = {}) {
  const override = getCategorySeoOverride(slug || categoryName, isPtBr ? 'pt' : 'en')
  if (override) return override
  const count = marketCount || (isPtBr ? 'centenas de' : 'hundreds of')
  if (isPtBr) {
    return {
      title: `Previsões e apostas sobre ${categoryName} | Futuur`,
      description: `Encontre as previsões mais certeiras sobre ${categoryName} na Futuur — ${count} mercados. Melhores previsões, através da inteligência coletiva.`,
    }
  }
  return {
    title: `${categoryName} Forecasts and Predictions | Futuur`,
    description: `Browse ${count} crowd-powered ${categoryName} predictions on Futuur — better forecasts, driven by the wisdom of the crowd.`,
  }
}

/**
 * Brazil 2026 election landing. Copy targets the Brazilian keyword cluster
 * ("eleições 2026", "quem vai ganhar", "previsão eleições") and its English mirror.
 */
export function getBrazilElectionsSeoMeta({ isPtBr = false, leaderName, leaderPct } = {}) {
  const odds = leaderName && leaderPct ? ` ${leaderName} ${leaderPct}.` : ''
  if (isPtBr) {
    return {
      title: 'Eleições 2026: previsões e probabilidades ao vivo | Futuur',
      description:
        `Quem vai ganhar as eleições 2026?${odds} Probabilidades em tempo real do mercado de previsões da Futuur para presidente, governadores e senadores. Grátis para participar.`,
    }
  }
  return {
    title: 'Brazil Election 2026 Predictions & Live Odds | Futuur',
    description:
      `Who will win Brazil's 2026 election?${odds} Live probabilities from Futuur's prediction markets for president, state governors and senate races. Free to play.`,
  }
}

/**
 * How it works snippet. Kept off the hero line so the page can sell the product
 * while search results explain what a prediction market is.
 * @param {{ isPtBr?: boolean }} opts
 */
export function getHowItWorksSeoMeta({ isPtBr = false } = {}) {
  if (isPtBr) {
    return {
      title: 'Como funcionam os mercados de previsão | Futuur',
      description:
        'Um mercado de previsão deixa a multidão negociar eventos futuros, e o preço vira a probabilidade. Veja como a Futuur funciona com play money ou dinheiro real.',
    }
  }
  return {
    title: 'How Prediction Markets Work | Futuur',
    description:
      'A prediction market lets the crowd trade on future events, and the price becomes the odds. See how Futuur markets work with play money or real money.',
  }
}

/**
 * Strip HTML tags for meta / JSON-LD plain text.
 * @param {string} html
 */
export function stripHtmlToText(html) {
  if (typeof html !== 'string') return ''
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}
